/**
 * Contracts & Claims — portfolio navigator.
 *
 * One page, four depths: Portfolio → Project → Vendor → Contract. The depth is
 * held in the URL (?project=&vendor=&contract=) so a drill-down can be shared,
 * bookmarked and survives a refresh. Every level is served from a single
 * `GET /api/contracts/portfolio/hierarchy` payload — drilling never refetches.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Input } from '@/components/ui/input';
import { QuickLaunchRail } from '@/components/contracts/portfolio/QuickLaunchRail';
import { DrillBreadcrumb } from '@/components/contracts/portfolio/DrillBreadcrumb';
import { LevelPortfolio } from '@/components/contracts/portfolio/LevelPortfolio';
import { LevelProject } from '@/components/contracts/portfolio/LevelProject';
import { LevelVendor } from '@/components/contracts/portfolio/LevelVendor';
import { LevelContract } from '@/components/contracts/portfolio/LevelContract';
import { Metric, fmtINR } from '@/components/contracts/portfolio/portfolioUi';
import { NewContractDialog, AddVendorDialog, MoveVendorDialog } from '@/components/contracts/portfolio/ContractDialogs';

export default function ContractsPortfolio() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tree, setTree] = useState(null);
  const [roll, setRoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [newContractOpen, setNewContractOpen] = useState(false);
  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [h, p] = await Promise.all([
        api.get('/contracts/portfolio/hierarchy'),
        api.get('/contracts/portfolio'),
      ]);
      setTree(h.data);
      setRoll(p.data);
    } catch { toast.error('Failed to load the contracts portfolio'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const projectId = params.get('project');
  const vendorName = params.get('vendor');
  const contractId = params.get('contract');

  const projects = useMemo(() => {
    const all = tree?.projects || [];
    const t = q.trim().toLowerCase();
    if (!t) return all;
    return all
      .map(p => {
        const vendors = p.vendors
          .map(v => ({ ...v, contracts: v.contracts.filter(c =>
            [c.title, c.contract_number, c.type].filter(Boolean).some(x => String(x).toLowerCase().includes(t))) }))
          .filter(v => v.vendor.toLowerCase().includes(t) || v.contracts.length);
        return { ...p, vendors };
      })
      .filter(p => p.project_name.toLowerCase().includes(t) || p.vendors.length);
  }, [tree, q]);

  const project = useMemo(() => projects.find(p => p.project_id === projectId) || null, [projects, projectId]);
  const vendor = useMemo(() => project?.vendors.find(v => v.vendor === vendorName) || null, [project, vendorName]);
  const contract = useMemo(() => vendor?.contracts.find(c => c.id === contractId) || null, [vendor, contractId]);

  const level = contract ? 'contract' : vendor ? 'vendor' : project ? 'project' : 'portfolio';

  const trail = [
    { key: 'p0', level: 'portfolio', name: tree?.portfolio_name || 'Portfolio' },
    ...(project ? [{ key: 'p1', level: 'project', name: project.project_name }] : []),
    ...(vendor ? [{ key: 'p2', level: 'vendor', name: vendor.vendor }] : []),
    ...(contract ? [{ key: 'p3', level: 'contract', name: contract.contract_number || contract.title }] : []),
  ];

  const jump = (index) => {
    const next = new URLSearchParams(params);
    if (index === 0) { next.delete('project'); next.delete('vendor'); next.delete('contract'); }
    if (index === 1) { next.delete('vendor'); next.delete('contract'); }
    if (index === 2) { next.delete('contract'); }
    setParams(next, { replace: false });
  };

  const setLevel = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next);
  };

  // KPI strip reflects the level you are standing on, not always the portfolio.
  const kpis = useMemo(() => {
    const w = roll?.wow || {};
    if (level === 'portfolio') {
      const k = roll?.kpis || {};
      return [
        { label: 'Contracts', value: k.contracts_total ?? 0, sub: `${k.contracts_active ?? 0} active`, accent: '#1E4E8C', delta: w.contracts?.delta_pct, deltaLabel: 'WoW' },
        { label: 'Portfolio value', value: fmtINR(k.total_value), sub: `${k.projects_covered ?? 0} projects`, accent: '#15803D' },
        { label: 'Open claims', value: k.open_claims ?? 0, sub: fmtINR(k.total_claim_exposure), accent: '#B45309', delta: w.claims?.delta_pct, deltaLabel: 'WoW', invert: true },
        { label: 'LD exposure', value: fmtINR(k.ld_exposure), sub: 'Cumulative accrual', accent: '#B91C1C', delta: w.ld_exposure?.delta_pct, deltaLabel: 'WoW', invert: true },
        { label: 'Variations', value: k.variations_total ?? 0, sub: 'Logged', accent: '#7C3AED', delta: w.variations?.delta_pct, deltaLabel: 'WoW', invert: true },
        { label: 'Disputes', value: k.disputes_active ?? 0, sub: 'Unresolved', accent: '#EA580C' },
      ];
    }
    const node = contract || vendor || project;
    if (!node) return [];
    const base = [
      { label: 'Value', value: node.value ? fmtINR(node.value) : 'Not set', sub: level === 'contract' ? node.type : `${node.contracts_count} contracts`, accent: '#15803D' },
      { label: 'Avg risk', value: (level === 'contract' ? node.risk_score : node.avg_risk) ?? '—', sub: level === 'contract' ? 'Cognara score' : `${node.red_flags ?? 0} red flags`, accent: '#B91C1C' },
      { label: 'LD exposure', value: fmtINR(node.ld_exposure), sub: 'Accrued', accent: '#B45309' },
      { label: 'Claims', value: node.claims ?? 0, sub: `${node.variations ?? 0} variations`, accent: '#EA580C' },
      { label: 'Documents', value: node.documents ?? 0, sub: 'On file', accent: '#1E4E8C' },
      { label: 'Invoices linked', value: node.linked_invoices ?? 0, sub: level === 'contract' && node.matching_invoices > node.linked_invoices ? `${node.matching_invoices - node.linked_invoices} to review` : 'From IPT register', accent: '#7C3AED' },
    ];
    return base;
  }, [level, roll, project, vendor, contract]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--brand-accent)' }} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 pb-10" data-testid="contracts-portfolio">
        <QuickLaunchRail />

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <DrillBreadcrumb trail={trail} onJump={jump} />
            <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
              {level === 'portfolio' && 'Every contract across every project — drill into a project to see who you are contracted with.'}
              {level === 'project' && 'Vendors on this project, ranked by committed value.'}
              {level === 'vendor' && 'Contracts held with this vendor on this project.'}
              {level === 'contract' && 'File documents and link the invoices raised against this contract.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a project, vendor or contract…"
                     className="pl-9 h-9 text-sm" data-testid="portfolio-search" />
            </div>
            <button onClick={() => load(true)} className="rounded-full border h-9 w-9 inline-flex items-center justify-center"
                    style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }} data-testid="portfolio-refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="portfolio-kpis">
          {kpis.map((k, i) => (
            <Metric key={k.label} {...k} index={i} testId={`portfolio-kpi-card-${k.label.replace(/\s+/g, '-').toLowerCase()}`} />
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={level + (contractId || vendorName || projectId || '')}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            aria-live="polite"
          >
            {level === 'portfolio' && (
              <LevelPortfolio
                projects={projects}
                topRisk={roll?.top_contracts_by_risk || []}
                onOpenProject={(p) => setLevel({ project: p.project_id, vendor: '', contract: '' })}
                onOpenContract={(id) => navigate(`/contracts/${id}`)}
              />
            )}
            {level === 'project' && (
              <LevelProject
                project={project}
                onOpenVendor={(v) => setLevel({ vendor: v.vendor, contract: '' })}
                onAddVendor={() => setAddVendorOpen(true)}
              />
            )}
            {level === 'vendor' && (
              <LevelVendor
                vendor={vendor}
                onOpenContract={(c) => setLevel({ contract: c.id })}
                onAddContract={() => setNewContractOpen(true)}
                onMoveContract={(c) => setMoveTarget(c)}
              />
            )}
            {level === 'contract' && (
              <LevelContract
                summary={contract}
                projectId={project?.project_id}
                vendors={(project?.vendors || []).map(v => v.vendor)}
                onChanged={() => load(true)}
              />
            )}
          </motion.div>
        </AnimatePresence>

        <AddVendorDialog
          open={addVendorOpen} onOpenChange={setAddVendorOpen}
          projectId={project?.project_id} projectName={project?.project_name}
          onAdded={async (v) => { await load(true); setLevel({ vendor: v, contract: '' }); }}
        />
        <NewContractDialog
          open={newContractOpen} onOpenChange={setNewContractOpen}
          projectId={project?.project_id} projectName={project?.project_name}
          vendor={vendor?.vendor}
          suggestedNumber={`CON-${(project?.project_id || '').replace(/[^0-9a-zA-Z]/g, '').slice(-3)}-${String((project?.contracts_count || 0) + 1).padStart(2, '0')}`}
          onCreated={() => load(true)}
        />
        <MoveVendorDialog
          open={!!moveTarget} onOpenChange={(o) => !o && setMoveTarget(null)}
          contract={moveTarget || {}} projectId={project?.project_id}
          vendors={(project?.vendors || []).map(v => v.vendor)}
          onMoved={() => { setMoveTarget(null); load(true); }}
        />
      </div>
    </DashboardLayout>
  );
}
